import React, { useState } from 'react';
import { PersonFinal } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/components/use-toast';

const CLIENT_DATA = [
  { person_id: "carolyn-chong-a7684010", client_name: "Swinburne University of Technology; RMIT University; Griffith University" },
  { person_id: "jan-clohessy-29aa7649", client_name: "RMIT University" },
  { person_id: "dr-hormoz-ahmadi-7a761a11b", client_name: "Australian Catholic University" },
  { person_id: "liann-correia", client_name: "RMIT University" },
  { person_id: "joshteichman", client_name: "RMIT University" },
  { person_id: "david-dennis-64485419", client_name: "Queensland University of Technology" },
  { person_id: "kelly-galvin-phd-394613b7", client_name: "Torrens University Australia" },
  { person_id: "jiahejoechen", client_name: "Australian Catholic University; Swinburne University of Technology" },
  { person_id: "clare-pollock-707297124", client_name: "Flinders University" },
  { person_id: "kevin-dunn-53109477", client_name: "University of Newcastle" },
  { person_id: "catherine-renshaw-b7686651", client_name: "Australian Catholic University; University of Newcastle; University of Sydney" },
  { person_id: "dr-jeremy-kingsley-9990b4b3", client_name: "Swinburne University of Technology" },
  { person_id: "michael-baker-52487714", client_name: "Australian Catholic University; Edith Cowan University; University of Newcastle; University of Sydney" },
  { person_id: "sarah-lewis-77520134a", client_name: "University of Sydney" },
  { person_id: "professormaryannedever", client_name: "Australian National University; University of Newcastle" },
  { person_id: "peter-hoj-ac-50194587", client_name: "University of Adelaide" },
  { person_id: "craig-batty-5013712b", client_name: "University of Technology Sydney; RMIT University" },
  { person_id: "matt-ma-a04aa1173", client_name: "University of Sydney" },
  { person_id: "michele-willson-0a351b14", client_name: "University of Melbourne" },
  { person_id: "samaneh-rastegari", client_name: "Edith Cowan University" },
  { person_id: "paulo-de-souza-070056199", client_name: "Griffith University" },
  { person_id: "michael-langhorne-b67620185", client_name: "RMIT University" },
  { person_id: "susan-rowland-8a477937", client_name: "University of Sydney" },
  { person_id: "grant-drummond", client_name: "University of Melbourne" },
  { person_id: "sarah-midford-615a8a34", client_name: "University of Melbourne" },
  { person_id: "michael-donovan-21266174", client_name: "University of Newcastle" },
  { person_id: "dr-andrea-carson", client_name: "University of Melbourne" },
  { person_id: "tracey-carpenter-85423117", client_name: "University of Melbourne" },
  { person_id: "monica-thielking", client_name: "Swinburne University of Technology" },
  { person_id: "laura-anne-bull-919b847a", client_name: "University of Newcastle; Australian National University" },
  { person_id: "suresh-cuganesan-57160258", client_name: "Swinburne University of Technology; University of Sydney" },
  { person_id: "professor-emma-johnston-ao-faa-ftse-1bb2ba118", client_name: "University of Sydney" },
  { person_id: "belinda-tynan-77ba5056", client_name: "RMIT University" },
  { person_id: "shane-dawson-84bb5711", client_name: "Queensland University of Technology" },
  { person_id: "william-wragg", client_name: "Griffith University" },
  { person_id: "benjamin-harris-3bab85118", client_name: "University of the Sunshine Coast" },
  { person_id: "professor-reuben-bolt-6b1584144", client_name: "University of Sydney" },
  { person_id: "kathy-refshauge-3bb9b732", client_name: "University of Sydney" },
  { person_id: "kimberley-williams-8b504846", client_name: "University of Technology Sydney" },
  { person_id: "jennifer-dowling-04a1804", client_name: "University of Sydney" },
  { person_id: "olivia-perks-23379235", client_name: "University of Sydney" },
  { person_id: "sally-chik", client_name: "Australian Catholic University" },
  { person_id: "renae-ryan-am-frsn-70097987", client_name: "University of Sydney" },
  { person_id: "heidi-norman-72a173b6", client_name: "University of Technology Sydney" }
];

export default function ClientNameUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [results, setResults] = useState([]);
  const { toast } = useToast();

  const handleUpload = async () => {
    setIsUploading(true);
    setResults([]);
    
    const uploadResults = [];
    
    for (const data of CLIENT_DATA) {
      try {
        const people = await PersonFinal.filter({ person_id: data.person_id });
        
        if (people.length === 0) {
          uploadResults.push({
            person_id: data.person_id,
            status: 'not_found',
            message: 'Person not found'
          });
          continue;
        }
        
        const person = people[0];
        
        await PersonFinal.update(person.id, {
          client_name: data.client_name,
          worked_for_customer: true
        });
        
        uploadResults.push({
          person_id: data.person_id,
          status: 'success',
          message: 'Updated successfully'
        });
      } catch (error) {
        uploadResults.push({
          person_id: data.person_id,
          status: 'error',
          message: error.message
        });
      }
    }
    
    setResults(uploadResults);
    setIsUploading(false);
    
    const successCount = uploadResults.filter(r => r.status === 'success').length;
    toast({
      title: "Upload Complete",
      description: `Successfully updated ${successCount} out of ${CLIENT_DATA.length} records.`
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-[#0A1A2F]">
            Client Name Bulk Upload
          </CardTitle>
          <p className="text-sm text-[#5C6B7A]">
            Upload client names for {CLIENT_DATA.length} personas who previously worked for existing clients.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              onClick={handleUpload}
              disabled={isUploading}
              className="bg-[#00C2C2] hover:bg-[#00A3A3] w-full"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Start Upload
                </>
              )}
            </Button>

            {results.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-[#0A1A2F] mb-3">Upload Results</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {results.map((result, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        result.status === 'success'
                          ? 'bg-green-50 border-green-200'
                          : result.status === 'not_found'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {result.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600" />
                        )}
                        <span className="text-sm font-medium">{result.person_id}</span>
                      </div>
                      <span className="text-xs text-[#5C6B7A]">{result.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}